import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import {
  Radar, Users, Send, RefreshCw, CheckCircle2, XCircle,
  Phone, Mail, MapPin, Building2, BadgeCheck, Loader2, MessageCircle, Globe2, MapPinned
} from 'lucide-react';
import AdminMarketTester from './AdminMarketTester';

const StatCard = ({ label, value, icon, accent = 'text-[#E07A3A]' }) => (
  <div className="bg-white border border-slate-200 px-5 py-4">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs tracking-[0.2em] uppercase text-slate-500">{label}</span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">{value ?? '—'}</div>
  </div>
);

export const AdminSovereignOS = () => {
  const [tab, setTab] = useState('hunt');
  const [stats, setStats] = useState(null);
  const [sample, setSample] = useState(null);
  const [applications, setApplications] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [running, setRunning] = useState(false);
  const [placesRunning, setPlacesRunning] = useState(null); // 'priority' | 'india' | null
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/sovereign/harvest-stats`);
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchSample = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/sovereign/hunt-sample?n_owners=5&n_pros=5`);
      setSample(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sample failed');
    } finally { setLoading(false); }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/sovereign/applications?status=pending_review&limit=50`);
      setApplications(r.data.items || []);
    } catch { toast.error('Could not load applications'); }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/sovereign/invitations?status=drafted&limit=50`);
      setInvitations(r.data.items || []);
    } catch { toast.error('Could not load invitations'); }
  }, []);

  useEffect(() => { fetchStats(); fetchSample(); }, [fetchStats, fetchSample]);
  useEffect(() => {
    if (tab === 'applications') fetchApplications();
    if (tab === 'invitations') fetchInvitations();
  }, [tab, fetchApplications, fetchInvitations]);

  const runDailyFeed = async (sendInvites) => {
    if (running) return;
    setRunning(true);
    toast.info('Hunting cycle started — this takes 2–3 minutes', { duration: 5000 });
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/sovereign/run-india-daily?send_invites=${sendInvites ? 'true' : 'false'}`
      );
      const s = r.data;
      toast.success(
        `Cycle complete: ${s.owners.total_new} owners, ${s.professionals.total_new} pros, ${s.invitations.sent} invites sent`,
        { duration: 8000 }
      );
      fetchStats(); fetchSample();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hunt failed');
    } finally { setRunning(false); }
  };

  const approveApp = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/sovereign/applications/${id}/approve`);
      toast.success('Approved & welcome email sent');
      fetchApplications(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const rejectApp = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/sovereign/applications/${id}/reject`);
      toast.success('Application rejected'); fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };
  const resendInvite = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/sovereign/invitations/${id}/send`);
      toast.success('Invitation queued'); fetchInvitations(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  // Google Places harvester — populates `professional_affiliate_leads`
  // which backs every /api/pro-leads, /api/directory/* and AI Network
  // panel query. Without this, IN + CA buyers see "no pros". Run this
  // once after a fresh deploy or when the directory looks thin.
  const runPlacesHarvest = async (kind) => {
    if (placesRunning) return;
    setPlacesRunning(kind);
    const endpoint = kind === 'priority'
      ? `${API_URL}/api/admin/harvest/run-priority`
      : `${API_URL}/api/admin/harvest/run-india-national?per_category=5`;
    const labelStart = kind === 'priority'
      ? 'Harvesting UK · IN · CA priority cities — 2-4 mins'
      : 'Scanning every Indian city — 3-6 mins';
    toast.info(labelStart, { duration: 6000 });
    try {
      const r = await axios.post(endpoint, {}, { timeout: 600000 });
      const s = r.data || {};
      const newRows = s.total_new ?? s.inserted ?? s.new_leads ?? s.pros_added ?? '—';
      toast.success(`Harvest complete · ${newRows} new pros added`, { duration: 9000 });
      fetchStats();
    } catch (e) {
      const err = e.response?.data?.detail || e.message || 'Harvest failed';
      if (typeof err === 'string' && err.includes('GOOGLE_PLACES_API_KEY')) {
        toast.error('Google Places API key missing on the backend. Set GOOGLE_PLACES_API_KEY and retry.', { duration: 9000 });
      } else {
        toast.error(err);
      }
    } finally { setPlacesRunning(null); }
  };

  const sendWhatsApp = async (id) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/sovereign/invitations/${id}/whatsapp`);
      toast.success(`WhatsApp queued (${r.data.status || 'sent'})`);
      fetchInvitations(); fetchStats();
    } catch (e) {
      const err = e.response?.data?.detail || 'WhatsApp send failed';
      // Friendly hint for the most common Twilio sandbox error
      if (typeof err === 'string' && err.includes('63007')) {
        toast.error('Recipient not in sandbox. They must send "join <code>" to +1 415 523 8886 first.', { duration: 8000 });
      } else if (typeof err === 'string' && err.includes('cooldown')) {
        toast.error('30-day cooldown — already messaged recently.');
      } else {
        toast.error(err);
      }
    }
  };

  const subTabs = [
    { id: 'hunt', label: 'Hunt Sample' },
    { id: 'applications', label: `Applications${stats?.pending_applications ? ` (${stats.pending_applications})` : ''}` },
    { id: 'invitations', label: `Invitations${stats?.invitations_drafted ? ` (${stats.invitations_drafted})` : ''}` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-sovereign">
      {/* Owner-only utility: switch detected market for this tab so the
          IN / CA flows can be validated from anywhere without a VPN. */}
      <AdminMarketTester />

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs tracking-[0.3em] uppercase text-[#E07A3A] block mb-1 font-medium">India Sovereign OS</span>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Autonomous Ecosystem Feeder</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Daily hunt for direct-owner sellers (NoBroker) and RERA-verified professionals (IndiaMART, state RERA).
            Auto-drafts DPDP-compliant Resend invitations with magic-link signup → Stripe panel-join checkout.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => runDailyFeed(false)} disabled={running}
            className="px-4 py-2.5 text-sm tracking-wider uppercase font-semibold border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="btn-run-hunt"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
            {running ? 'Hunting…' : 'Run Hunt Cycle'}
          </button>
          <button
            onClick={() => runDailyFeed(true)} disabled={running}
            className="px-4 py-2.5 text-sm tracking-wider uppercase font-semibold bg-[#E07A3A] text-white hover:bg-[#C4652E] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="btn-run-hunt-with-invites"
          >
            <Send className="h-4 w-4" />
            Hunt + Send Invites
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Owner Listings" value={stats?.in_owner_listings} icon={<Building2 className="h-4 w-4" />} />
        <StatCard label="Pending Apps" value={stats?.pending_applications} icon={<Users className="h-4 w-4" />} />
        <StatCard label="Drafted" value={stats?.invitations_drafted} icon={<Mail className="h-4 w-4" />} />
        <StatCard label="Sent" value={stats?.invitations_sent} icon={<Send className="h-4 w-4" />} />
        <StatCard label="Accepted" value={stats?.invitations_accepted} icon={<BadgeCheck className="h-4 w-4" />} accent="text-emerald-600" />
      </div>

      {/* Google Places Pro Harvester — populates the Pro directory used
          by /api/pro-leads. After a fresh deploy production starts empty
          for IN + CA, so kick this once to backfill. */}
      <section className="bg-amber-50 border border-amber-200 rounded p-4" data-testid="places-harvest-panel">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-700 block mb-1">
              Global Pro Harvester · Google Places
            </span>
            <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A]">Populate the Pro directory</h3>
            <p className="text-xs text-slate-600 mt-1 max-w-xl">
              Backs every <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">/api/pro-leads</code>{' '}
              query. If buyers in India / Canada see "no pros", trigger a harvest below — IN + CA cities are scanned with the same Google Places API as UK.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => runPlacesHarvest('priority')}
              disabled={!!placesRunning}
              className="px-4 py-2.5 text-xs tracking-wider uppercase font-semibold border-2 border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white rounded transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="btn-places-harvest-priority"
            >
              {placesRunning === 'priority' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe2 className="h-4 w-4" />}
              {placesRunning === 'priority' ? 'Harvesting…' : 'Run UK · IN · CA harvest'}
            </button>
            <button
              onClick={() => runPlacesHarvest('india')}
              disabled={!!placesRunning}
              className="px-4 py-2.5 text-xs tracking-wider uppercase font-semibold bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="btn-places-harvest-india"
            >
              {placesRunning === 'india' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPinned className="h-4 w-4" />}
              {placesRunning === 'india' ? 'Scanning India…' : 'Scan every Indian city'}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-amber-800/70">
          Requires <code>GOOGLE_PLACES_API_KEY</code> on the backend. Each run inserts new pros into{' '}
          <code className="bg-white px-1 rounded">professional_affiliate_leads</code> and refreshes the directory immediately — no deploy needed.
        </p>
      </section>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {subTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm tracking-wider uppercase font-medium transition-colors ${
              tab === t.id
                ? 'text-[#E07A3A] border-b-2 border-[#E07A3A] -mb-px'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`sov-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hunt' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Owner leads */}
          <section className="bg-white border border-slate-200">
            <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#E07A3A]" />
                <span className="font-semibold text-sm tracking-wide">Direct-Owner Sellers</span>
              </div>
              <span className="text-xs text-slate-400">Total: {sample?.owner_count_total ?? '—'}</span>
            </header>
            <div className="divide-y divide-slate-100">
              {loading && <div className="p-6 text-center text-slate-400 text-sm">Hunting…</div>}
              {!loading && (sample?.owners || []).map((o, i) => (
                <div key={o.id || o.source_url || `owner-${i}`} className="px-5 py-3.5" data-testid={`owner-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#1A1A1A] truncate">{o.title}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{o.city}</span>
                        {o.bedrooms ? <span>{o.bedrooms} BHK</span> : null}
                        <span className="text-[#E07A3A] font-semibold">{o.price_raw}</span>
                      </div>
                      {o.agent_contact_phone && (
                        <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                          <Phone className="h-3 w-3" />{o.agent_contact_phone}
                        </div>
                      )}
                    </div>
                    <a href={o.source_url} target="_blank" rel="noreferrer"
                      className="text-[10px] tracking-wider uppercase text-slate-400 hover:text-[#E07A3A] whitespace-nowrap">
                      {o.source}↗
                    </a>
                  </div>
                </div>
              ))}
              {!loading && !(sample?.owners?.length) && (
                <div className="p-6 text-center text-slate-400 text-sm">No owner leads yet — run hunt cycle.</div>
              )}
            </div>
          </section>

          {/* Professional leads */}
          <section className="bg-white border border-slate-200">
            <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-[#E07A3A]" />
                <span className="font-semibold text-sm tracking-wide">Verified Professionals</span>
              </div>
              <span className="text-xs text-slate-400">Total: {sample?.professional_count_total ?? '—'}</span>
            </header>
            <div className="divide-y divide-slate-100">
              {loading && <div className="p-6 text-center text-slate-400 text-sm">Hunting…</div>}
              {!loading && (sample?.professionals || []).map((p, i) => (
                <div key={p.id || p.registration_number || `pro-${i}`} className="px-5 py-3.5" data-testid={`pro-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#1A1A1A] truncate">{p.name}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city}</span>
                        <span className="text-[10px] tracking-wider uppercase bg-slate-100 px-1.5 py-0.5">
                          {p.professional_type?.replace('_', ' ')}
                        </span>
                        {p.registration_number && (
                          <span className="text-[10px] tracking-wider uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5">
                            RERA {p.registration_number.slice(0, 12)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-600 mt-1">
                        {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                        {p.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                      </div>
                    </div>
                    <span className="text-[10px] tracking-wider uppercase text-slate-400 whitespace-nowrap">
                      {p.source}
                    </span>
                  </div>
                </div>
              ))}
              {!loading && !(sample?.professionals?.length) && (
                <div className="p-6 text-center text-slate-400 text-sm">No professionals yet — run hunt cycle.</div>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === 'applications' && (
        <section className="bg-white border border-slate-200">
          <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-sm tracking-wide">Pending Professional Applications</span>
            <button onClick={fetchApplications} className="text-slate-400 hover:text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
          </header>
          <div className="divide-y divide-slate-100">
            {applications.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-sm">No pending applications.</div>
            )}
            {applications.map(a => (
              <div key={a.id} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`app-row-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{a.name || a.email}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.email} · {a.phone || '—'}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-2">
                    <span>{a.professional_type}</span>
                    <span>·</span>
                    <span>{a.city || '—'}, {a.country}</span>
                    {a.registration_number && <><span>·</span><span>Reg: {a.registration_number}</span></>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveApp(a.id)}
                    className="px-3 py-1.5 text-xs tracking-wider uppercase font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                    data-testid={`btn-approve-${a.id}`}>
                    <CheckCircle2 className="h-3 w-3" />Approve
                  </button>
                  <button onClick={() => rejectApp(a.id)}
                    className="px-3 py-1.5 text-xs tracking-wider uppercase font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                    data-testid={`btn-reject-${a.id}`}>
                    <XCircle className="h-3 w-3" />Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'invitations' && (
        <section className="bg-white border border-slate-200">
          <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-sm tracking-wide">Drafted Invitations</span>
            <button onClick={fetchInvitations} className="text-slate-400 hover:text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
          </header>
          <div className="divide-y divide-slate-100">
            {invitations.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-sm">No drafted invitations.</div>
            )}
            {invitations.map(iv => (
              <div key={iv.id} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`inv-row-${iv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{iv.name || '—'}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{iv.email || iv.phone}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {iv.professional_type} · {iv.city || '—'} · {iv.source}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => resendInvite(iv.id)}
                    disabled={!iv.email}
                    className="px-3 py-1.5 text-xs tracking-wider uppercase font-semibold bg-[#E07A3A] text-white hover:bg-[#C4652E] disabled:opacity-40 flex items-center gap-1"
                    data-testid={`btn-send-${iv.id}`}>
                    <Send className="h-3 w-3" />{iv.email ? 'Email' : 'No Email'}
                  </button>
                  <button onClick={() => sendWhatsApp(iv.id)}
                    disabled={!iv.phone}
                    className="px-3 py-1.5 text-xs tracking-wider uppercase font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1"
                    data-testid={`btn-whatsapp-${iv.id}`}>
                    <MessageCircle className="h-3 w-3" />{iv.phone ? 'WhatsApp' : 'No Phone'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
